import { Check, CircleSlash, PackagePlus, Pencil } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import type { StockArticle } from "@/lib/stock.api";
import { cn } from "@/lib/utils";
import { formatMoney } from "./stock-formatters";
import { StockArticleStatusBadge } from "./components/StockBadges";

export function createStockArticleColumns({
  canManage,
  toggling,
  onToggleActive,
  onEdit,
  onRestock,
}: {
  canManage: boolean;
  toggling: boolean;
  onToggleActive: (article: StockArticle) => void;
  onEdit: (article: StockArticle) => void;
  onRestock: (article: StockArticle) => void;
}): ColumnDef<StockArticle, any>[] {
  return [
    {
      accessorKey: "name",
      header: "Article",
      cell: ({ row }) => (
        <span className="text-[12px] text-body">
          {row.original.name} <span className="text-subtle">({row.original.unit})</span>
        </span>
      ),
    },
    {
      accessorKey: "onHand",
      header: "En stock",
      meta: { align: "right" },
      cell: ({ row }) => (
        <span
          className={cn(
            "text-[12px] font-medium tabular-nums",
            row.original.onHand < row.original.minLevel ? "text-warning-text" : "text-body",
          )}
        >
          {row.original.onHand}
        </span>
      ),
    },
    {
      accessorKey: "minLevel",
      header: "Seuil bas",
      meta: { align: "right" },
      cell: ({ row }) => (
        <span className="text-[12px] tabular-nums text-muted-foreground">
          {row.original.minLevel}
        </span>
      ),
    },
    {
      accessorKey: "defaultSellingPrice",
      header: "Prix vente",
      meta: { align: "right" },
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">
          {formatMoney(row.original.defaultSellingPrice)}
        </span>
      ),
    },
    {
      accessorKey: "active",
      header: "Statut",
      cell: ({ row }) => <StockArticleStatusBadge article={row.original} />,
    },
    ...(canManage
      ? [
          {
            id: "actions",
            header: "",
            meta: { align: "right" as const },
            cell: ({ row }: { row: { original: StockArticle } }) => (
              <div className="space-x-1 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(row.original);
                  }}
                  title="Modifier"
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRestock(row.original);
                  }}
                  title="Réapprovisionner"
                >
                  <PackagePlus size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleActive(row.original);
                  }}
                  title={row.original.active ? "Désactiver" : "Activer"}
                  disabled={toggling}
                >
                  {row.original.active ? <CircleSlash size={14} /> : <Check size={14} />}
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];
}
